<script lang="ts">
export default {
  name: 'PolicyTypeEditModal',
};
</script>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import Combobox from '@/components/v2/Combobox.vue';
import { AlertCircle } from 'lucide-vue-next';

interface PolicyType {
  policyType: string;
  policyDescription: string;
  coverageType: string;
  casualtyFlag: boolean;
}

const props = defineProps<{
  show: boolean;
  policyType: PolicyType | null;
  coverageTypes: string[];
}>();

const emit = defineEmits<{
  cancelModal: [];
  saveDescription: [policyType: PolicyType];
  saveCoverageType: [policyType: PolicyType, coverageType: string];
}>();

// Reactive state
const showModal = ref(false);
const loading = ref(false);
const error = ref('');

// Form fields
const description = ref(props.policyType?.policyDescription || '');
const selectedCoverageType = ref(props.policyType?.coverageType || '');
const newCoverageTypeName = ref('');
const isCreatingNew = ref(false);

// Computed
const coverageTypeOptions = computed(() => {
  if (!props.coverageTypes || !Array.isArray(props.coverageTypes)) {
    return [];
  }
  return props.coverageTypes.map((type) => ({
    value: type,
    label: type,
  }));
});

// Watch for prop changes to update form values
watch(
  () => props.policyType,
  (newPolicyType) => {
    if (!showModal.value && newPolicyType) {
      description.value = newPolicyType.policyDescription;
      selectedCoverageType.value = newPolicyType.coverageType;
    }
  },
);

watch(
  () => props.show,
  (newShow) => {
    if(newShow) {
      openModal();
    }
  },
);

// Methods
const openModal = () => {
  if (!props.policyType){
    console.error('No policy type provided to edit.');
    return;
  }

  description.value = props.policyType.policyDescription;
  selectedCoverageType.value = props.policyType.coverageType;
  newCoverageTypeName.value = '';
  isCreatingNew.value = false;
  error.value = '';
  showModal.value = true;
};

const handleCoverageTypeSelect = (option: { value: string; label: string }) => {
  selectedCoverageType.value = option.value;
};

const handleSave = async () => {
  if (!props.policyType) {
    error.value = 'No policy type to save';
    return;
  }

  try {
    loading.value = true;
    error.value = '';

    // Validate inputs
    if (!description.value.trim()) {
      error.value = 'Policy description cannot be empty';
      loading.value = false;
      return;
    }

    let coverageTypeToSave = '';

    if (isCreatingNew.value) {
      // Validate new coverage type name
      if (!newCoverageTypeName.value.trim()) {
        error.value = 'Coverage type name cannot be empty';
        loading.value = false;
        return;
      }

      if (props.coverageTypes.includes(newCoverageTypeName.value.trim())) {
        error.value = 'Coverage type already exists';
        loading.value = false;
        return;
      }

      coverageTypeToSave = newCoverageTypeName.value.trim();
    } else {
      if (!selectedCoverageType.value) {
        error.value = 'Please select a coverage type';
        loading.value = false;
        return;
      }
      coverageTypeToSave = selectedCoverageType.value;
    }

    // Create updated policy type object
    const updatedPolicyType = {
      ...props.policyType,
      policyDescription: description.value.trim(),
    };

    // Emit description change if it changed
    if (description.value.trim() !== props.policyType.policyDescription) {
      emit('saveDescription', updatedPolicyType);
    }

    // Emit coverage type change if it changed
    if (coverageTypeToSave !== props.policyType.coverageType) {
      emit('saveCoverageType', props.policyType, coverageTypeToSave);
    }

    showModal.value = false;
    loading.value = false;
  } catch (e) {
    console.error(e);
    loading.value = false;
  }
};

const handleCancel = () => {
  description.value = "";
  selectedCoverageType.value = "";
  newCoverageTypeName.value = '';
  isCreatingNew.value = false;
  error.value = '';
  showModal.value = false;
  emit('cancelModal');
};
</script>

<template>
  <div>
    <AppModal
      :show="showModal"
      :title="`Edit Policy Type: ${policyType?.policyType || ''}`"
      :loading="loading"
      actionButtonLabel="Save Changes"
      :handleAction="handleSave"
      :handleCancel="handleCancel"
      size="md">
      <template #content>
        <div class="tw:flex tw:flex-col tw:gap-4">
          <!-- Policy Description -->
          <div class="tw:w-full">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1.5">Policy Description</span>
            </label>
            <input
              type="text"
              v-model="description"
              class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
              :class="{ 'tw:border-red-500': error && !description.trim() }"
              placeholder="Enter policy description..." />
          </div>

          <!-- Coverage Type Selection -->
          <div v-if="!isCreatingNew" class="tw:w-full">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500 tw:pb-1.5">Coverage Type</span>
            </label>
            <Combobox
              v-model="selectedCoverageType"
              :options="coverageTypeOptions"
              :placeholder="'Select coverage type'"
              input-class="tw:input-md"
              :class="{ 'tw:border-red-500': error && !selectedCoverageType }"
              @selected="handleCoverageTypeSelect" />
          </div>

          <!-- New Coverage Type Creation -->
          <div v-else class="tw:w-full">
            <label class="tw:label">
              <span class="tw:label-text tw:text-xs tw:text-gray-500">New Coverage Type Name</span>
            </label>
            <input
              type="text"
              v-model="newCoverageTypeName"
              class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
              :class="{
                'tw:border-red-500':
                  error && (!newCoverageTypeName.trim() || props.coverageTypes.includes(newCoverageTypeName.trim())),
              }"
              placeholder="Enter new coverage type name..." />
          </div>

          <!-- Create New Toggle -->
          <div class="tw:flex tw:items-center tw:gap-3">
            <label class="tw:label tw:form-control tw:flex-row tw:gap-3">
              <input
                type="checkbox"
                v-model="isCreatingNew"
                class="tw:toggle tw:toggle-primary tw:border tw:border-solid tw:border-gray-400" />
              <span class="tw:label-text tw:text-sm">Create new coverage type</span>
            </label>
          </div>

          <!-- Error Display -->
          <div v-if="error" class="tw:rounded-md tw:border tw:border-red-200 tw:bg-red-50 tw:p-3">
            <div class="tw:flex">
              <div class="tw:shrink-0">
                <AlertCircle class="tw:h-5 tw:w-5 tw:text-red-400" />
              </div>
              <div class="tw:ml-3">
                <h3 class="tw:text-sm tw:font-medium tw:text-red-800">Error</h3>
                <div class="tw:mt-1 tw:text-sm tw:text-red-700">
                  {{ error }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </AppModal>
  </div>
</template>
